import Box from '@mui/material/Box'
import { styled } from '@mui/material/styles'
import {
  DataGrid,
  GridCallbackDetails,
  gridClasses,
  GridColDef,
  GridFilterPanel,
  GridInputRowSelectionModel,
  GridRowSelectionModel,
} from '@mui/x-data-grid'
import CustomToolbar, { exportButtonConfig } from './CustomToolbar'
import useDeviceSize, { DeviceSize } from 'hooks/useDeviceSize'

export const WrappedHeaderText: React.FC<{ headerName?: string }> = ({ headerName, ...props }) => {
  return (
    <Box
      sx={{
        whiteSpace: 'normal',
        lineHeight: '1.2',
        textAlign: 'center',
      }}
      {...props}
    >
      {headerName}
    </Box>
  )
}

const StyledFilterPanel = styled(GridFilterPanel)(({ theme }) => ({
  backgroundColor: '#fff',
  border: '1px solid #D0D5DD',
  borderRadius: 8,
  padding: theme.spacing(1, 2),
  boxShadow: 'none',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  '& .MuiFormControl-root': {
    marginBottom: 0,
  },
  '& .MuiInputLabel-root': {
    color: '#474747',
    fontSize: '14px',
    fontWeight: 500,
  },
  '& .MuiInputBase-root': {
    height: '44px',
    borderRadius: 8,
    backgroundColor: '#fff',
    border: '1px solid #E4E7EC',
    boxShadow: 1,
    fontSize: '14px',
    maxWidth: { xs: 150, md: 'none' },
  },
  '& .MuiInputBase-input': {
    padding: '6px 8px',
  },
  '& .MuiSelect-select': {
    paddingRight: '24px !important',
  },
  '& .MuiButton-root': {
    border: '1px solid #D0D5DD',
    backgroundColor: '#fff',
    borderRadius: 8,
    height: '44px',
    textTransform: 'none',
    fontSize: '14px',
    fontWeight: 500,
    color: 'var(--Headings, #474747)',
    padding: '6px 16px',
    '&.MuiButton-containedPrimary': {
      backgroundColor: '#1976d2',
      color: '#fff',
      '&:hover': {
        backgroundColor: '#1565c0',
      },
    },
    '&.MuiButton-outlinedPrimary': {
      borderColor: '#1976d2',
      color: '#1976d2',
      '&:hover': {
        borderColor: '#1565c0',
      },
    },
  },
}))

const TableComponent = (props: {
  header: GridColDef<any>[]
  body: any[]
  selectedIds: GridInputRowSelectionModel
  onSelectionChange: (rowSelectionModel: GridRowSelectionModel, details: GridCallbackDetails) => void
  onRegisterClick?: () => void
  onNotifyClick?: () => void
  onDeleteClick?: () => void
  csvOptions?: any
  printOptions?: any
  children?: React.ReactNode
}): JSX.Element => {
  const {
    header,
    body,
    selectedIds,
    onSelectionChange,
    onRegisterClick,
    onNotifyClick,
    onDeleteClick,
    csvOptions,
    printOptions,
    children,
  } = props
  const deviceSize: DeviceSize = useDeviceSize()
  const visibleFields = header.reduce((a: any, v: any) => ({ ...a, [v.field]: v?.visible ? true : false }), {})
  return (
    <DataGrid
      autoHeight
      slots={{
        toolbar: () => (
          <CustomToolbar
            rowsSelected={Array.isArray(selectedIds) ? selectedIds.length : selectedIds ? 1 : 0}
            onRegisterClick={onRegisterClick}
            onNotifyClick={onNotifyClick}
            onDeleteClick={onDeleteClick}
            csvOptions={csvOptions}
            printOptions={printOptions}
          >
            {children}
          </CustomToolbar>
        ),
        filterPanel: StyledFilterPanel,
      }}
      localeText={{
        toolbarFilters: '',
        toolbarExport: exportButtonConfig[deviceSize].text,
      }}
      disableColumnMenu={true}
      disableColumnSelector={true}
      disableColumnFilter={false}
      disableDensitySelector={true}
      disableRowSelectionOnClick={true}
      checkboxSelection={true}
      indeterminateCheckboxAction={'select'}
      density='comfortable'
      columnHeaderHeight={44}
      rows={body}
      columns={header}
      pagination={true}
      // rows selection event
      rowSelectionModel={selectedIds}
      onRowSelectionModelChange={onSelectionChange}
      initialState={{
        columns: { columnVisibilityModel: visibleFields },
        sorting: {
          sortModel: [{ field: 'registered', sort: 'desc' }],
        },
      }}
      sortingOrder={['asc', 'desc']} // Applies globally to all columns
      data-testid='users-row-selection-checkbox'
      sx={{
        // Style for table - rounded corners, and no borders
        borderRadius: '20px',
        // Style for column header - no separator, light gray background
        [`& .${gridClasses.columnHeader}`]: { border: 'none !important', backgroundColor: '#F5F5F5' },
        [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]: { outline: 'none' },
        [`& .${gridClasses.columnSeparator}`]: {
          [`&:not(.${gridClasses['columnSeparator--resizable']})`]: { display: 'none' },
        },
        // Style for rows - white background, light gray background on hover, light yellow background on selection
        [`& .${gridClasses.cell}`]: { border: 'none' },
        [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: { outline: 'none' },
        [`& .${gridClasses.row}`]: { backgroundColor: '#FFF' },
        [`& .${gridClasses.row}:hover`]: { backgroundColor: '#F5F5F5' },
        [`& .${gridClasses.row}.Mui-selected`]: { backgroundColor: '#FFFDF6 !important' },
        // Style for checkbox - yellow color when checked
        '& .MuiCheckbox-root svg': { width: 20, height: 20 },
        '& .MuiCheckbox-root.MuiCheckbox-indeterminate': { color: '#F0E977' },
        '& .MuiCheckbox-root.Mui-checked path': { fill: '#F0E977' },
        // Style for the footer - no border, white background
        [`& .${gridClasses.footerContainer}`]: { border: 'none', backgroundColor: '#FFF' },
      }}
    />
  )
}

export default TableComponent
